import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { offboardingAPI, hrDocsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Loader2, ClipboardList, CheckCircle2, Circle, Download, FileText, Calculator, Save, MinusCircle, Send, ThumbsUp, ThumbsDown, Lock, BadgeCheck, Undo2 } from 'lucide-react';

const STATUS_COLORS = { in_progress: 'secondary', completed: 'default' };
const CATEGORY_LABELS = { kt: 'Knowledge Transfer', asset: 'Asset Return', access: 'Access Revoke', finance: 'Finance', hr: 'HR Documents', interview: 'Interview' };
const FF_STATUS_META = {
  draft:             { label: 'Draft',             color: 'outline',     icon: FileText },
  proposed:          { label: 'Proposed',          color: 'secondary',   icon: Send },
  finance_approved:  { label: 'Finance Approved',  color: 'default',     icon: BadgeCheck },
  finalized:         { label: 'Finalized',         color: 'default',     icon: Lock },
  paid:              { label: 'Paid',              color: 'default',     icon: CheckCircle2 },
  rejected:          { label: 'Rejected',          color: 'destructive', icon: ThumbsDown },
};

const Offboarding = () => {
  const { user } = useAuth();
  const allowed = !user || ['super_admin', 'hr_admin', 'manager', 'payroll_admin'].includes(user?.role);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // full offboarding doc
  const [detailLoading, setDetailLoading] = useState(false);

  // F&F dialog state
  const [ffDialog, setFFDialog] = useState(false);
  const [ffForm, setFFForm] = useState({ unused_leave_days: 0, pending_bonus: 0, dues: 0, notice_recovery_days: 0 });
  const [ffResult, setFFResult] = useState(null);
  const [ffLoading, setFFLoading] = useState(false);
  const [ffWorkflow, setFFWorkflow] = useState(null);
  const [ffActionLoading, setFFActionLoading] = useState(false);
  const [paidDialog, setPaidDialog] = useState(false);
  const [paidForm, setPaidForm] = useState({ paid_reference: '', payment_method: 'bank_transfer', paid_date: '', notes: '' });
  const [decisionComments, setDecisionComments] = useState('');

  const fetchList = async () => {
    setLoading(true);
    try {
      const r = await offboardingAPI.list();
      setList(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (allowed) fetchList(); /* eslint-disable-next-line */ }, [allowed]);

  const openDetail = async (empId) => {
    setDetailLoading(true);
    try {
      const r = await offboardingAPI.get(empId);
      setSelected(r.data);
      setFFResult(r.data.ff_settlement || null);
      try {
        const w = await offboardingAPI.ffWorkflow(empId);
        setFFWorkflow(w.data.workflow);
      } catch (e) { setFFWorkflow(null); }
    } catch (e) { toast.error('Failed to load details'); }
    finally { setDetailLoading(false); }
  };

  const refreshWorkflow = async (empId) => {
    try { const w = await offboardingAPI.ffWorkflow(empId); setFFWorkflow(w.data.workflow); openDetail(empId); fetchList(); } catch (e) { /* ignore */ }
  };

  const ffAction = async (action, payload) => {
    if (!selected) return;
    setFFActionLoading(true);
    try {
      const map = { propose: offboardingAPI.ffPropose, approve: () => offboardingAPI.ffDecide(selected.employee_id, { decision: 'approve', comments: decisionComments }), reject: () => offboardingAPI.ffDecide(selected.employee_id, { decision: 'reject', comments: decisionComments }), finalize: offboardingAPI.ffFinalize, withdraw: offboardingAPI.ffWithdraw, mark_paid: () => offboardingAPI.ffMarkPaid(selected.employee_id, paidForm) };
      const fn = map[action];
      if (action === 'propose' || action === 'finalize' || action === 'withdraw') await fn(selected.employee_id, { comments: decisionComments });
      else await fn();
      toast.success(`F&F ${action.replace('_', ' ')}d`);
      setDecisionComments('');
      if (action === 'mark_paid') setPaidDialog(false);
      refreshWorkflow(selected.employee_id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setFFActionLoading(false); }
  };

  const updateTask = async (taskKey, status, notes) => {
    if (!selected) return;
    try {
      await offboardingAPI.updateTask(selected.employee_id, { task_key: taskKey, status, notes: notes || null });
      toast.success(`Task ${status}`);
      openDetail(selected.employee_id);
      fetchList();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const calculateFF = async (save = false) => {
    if (!selected) return;
    setFFLoading(true);
    try {
      const r = await offboardingAPI.calculateFF(selected.employee_id, { ...ffForm, save });
      setFFResult(r.data);
      if (save) {
        toast.success('F&F settlement saved');
        openDetail(selected.employee_id);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setFFLoading(false); }
  };

  const downloadFFPdf = async () => {
    if (!selected) return;
    try {
      const r = await offboardingAPI.downloadFFPdf(selected.employee_id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `FF_Settlement_${selected.employee_id}.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch (e) { toast.error('Failed to download'); }
  };

  if (!allowed) return <Navigate to="/" replace />;
  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const inProgress = list.filter(o => o.status === 'in_progress');
  const completed = list.filter(o => o.status === 'completed');

  return (
    <div className="space-y-6" data-testid="offboarding-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><ClipboardList />Offboarding</h1>
        <p className="text-muted-foreground text-sm">Manage exit checklists, knowledge transfer, exit interviews and Full &amp; Final settlements</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">In Progress</p><p className="text-2xl font-bold text-amber-600">{inProgress.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Completed</p><p className="text-2xl font-bold text-emerald-600">{completed.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Total</p><p className="text-2xl font-bold">{list.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Pending Tasks</p><p className="text-2xl font-bold text-red-600">{list.reduce((acc, o) => acc + (o.progress.total - o.progress.completed), 0)}</p></CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Trigger</TableHead>
              <TableHead>Last Working Day</TableHead>
              <TableHead>Progress</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No offboarding records — approved resignations and terminations will appear here automatically.</TableCell></TableRow>}
              {list.map(o => (
                <TableRow key={o.id} data-testid={`offboarding-row-${o.employee_id}`}>
                  <TableCell className="font-medium">{o.employee_name}<div className="text-xs text-muted-foreground">{o.employee_code} · {o.designation}</div></TableCell>
                  <TableCell><Badge variant={o.triggered_by === 'termination' ? 'destructive' : 'secondary'}>{o.triggered_by}</Badge></TableCell>
                  <TableCell>{o.last_working_day || '—'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Progress value={o.progress.pct} className="w-24 h-2" />
                      <span className="text-xs text-muted-foreground">{o.progress.completed}/{o.progress.total}</span>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant={STATUS_COLORS[o.status]}>{o.status}</Badge></TableCell>
                  <TableCell><Button size="sm" variant="outline" onClick={() => openDetail(o.employee_id)} data-testid={`view-offboarding-${o.employee_id}`}>Manage</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2">Offboarding — {selected?.employee_id}</DialogTitle></DialogHeader>
          {detailLoading ? <Loader2 className="animate-spin" /> : selected && (
            <Tabs defaultValue="checklist">
              <TabsList>
                <TabsTrigger value="checklist" data-testid="tab-checklist">Checklist</TabsTrigger>
                <TabsTrigger value="ff" data-testid="tab-ff">Full &amp; Final</TabsTrigger>
                <TabsTrigger value="interview" data-testid="tab-interview">Exit Interview</TabsTrigger>
                <TabsTrigger value="kt" data-testid="tab-kt">KT Documents</TabsTrigger>
              </TabsList>

              <TabsContent value="checklist">
                <div className="space-y-3">
                  {Object.entries(selected.tasks.reduce((acc, t) => { (acc[t.category] = acc[t.category] || []).push(t); return acc; }, {})).map(([cat, items]) => (
                    <Card key={cat}>
                      <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">{CATEGORY_LABELS[cat] || cat}</CardTitle></CardHeader>
                      <CardContent className="space-y-2">
                        {items.map(t => (
                          <div key={t.key} className="flex items-center justify-between border-l-2 pl-3 py-1" style={{borderColor: t.status === 'completed' ? '#10b981' : t.status === 'na' ? '#94a3b8' : '#fbbf24'}}>
                            <div className="flex items-center gap-2">
                              {t.status === 'completed' ? <CheckCircle2 size={16} className="text-emerald-600" /> : t.status === 'na' ? <MinusCircle size={16} className="text-slate-400" /> : <Circle size={16} className="text-amber-500" />}
                              <div>
                                <div className="text-sm font-medium">{t.label}</div>
                                {t.completed_by && <div className="text-xs text-muted-foreground">by {t.completed_by} on {new Date(t.completed_at).toLocaleDateString()}</div>}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              {t.status !== 'completed' && <Button size="sm" variant="outline" onClick={() => updateTask(t.key, 'completed')} data-testid={`complete-${t.key}`}>Complete</Button>}
                              {t.status !== 'na' && <Button size="sm" variant="ghost" onClick={() => updateTask(t.key, 'na')}>N/A</Button>}
                              {t.status !== 'pending' && <Button size="sm" variant="ghost" onClick={() => updateTask(t.key, 'pending')}>Reopen</Button>}
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="ff">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2"><Calculator size={18} />Full &amp; Final Settlement</span>
                      {ffWorkflow && (() => { const M = FF_STATUS_META[ffWorkflow.status || 'draft']; const I = M.icon; return (
                        <Badge variant={M.color} className="flex items-center gap-1" data-testid="ff-status-badge"><I size={12} />{M.label}</Badge>
                      ); })()}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Calculator: editable only in draft/rejected */}
                    {(!ffWorkflow || ffWorkflow.status === 'draft' || ffWorkflow.status === 'rejected') ? (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label>Unused Leave Days</Label><Input type="number" value={ffForm.unused_leave_days} onChange={(e) => setFFForm({...ffForm, unused_leave_days: parseFloat(e.target.value) || 0})} data-testid="ff-leave-days" /></div>
                          <div><Label>Pending Bonus</Label><Input type="number" value={ffForm.pending_bonus} onChange={(e) => setFFForm({...ffForm, pending_bonus: parseFloat(e.target.value) || 0})} data-testid="ff-bonus" /></div>
                          <div><Label>Outstanding Dues</Label><Input type="number" value={ffForm.dues} onChange={(e) => setFFForm({...ffForm, dues: parseFloat(e.target.value) || 0})} data-testid="ff-dues" /></div>
                          <div><Label>Notice Recovery Days</Label><Input type="number" value={ffForm.notice_recovery_days} onChange={(e) => setFFForm({...ffForm, notice_recovery_days: parseFloat(e.target.value) || 0})} data-testid="ff-notice" /></div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button onClick={() => calculateFF(false)} disabled={ffLoading} data-testid="calculate-ff-btn"><Calculator size={14} className="mr-1" />Calculate</Button>
                          <Button onClick={() => calculateFF(true)} disabled={ffLoading || !ffResult} variant="default" data-testid="save-ff-btn"><Save size={14} className="mr-1" />Save Draft</Button>
                          {ffResult && <Button onClick={downloadFFPdf} variant="outline" data-testid="download-ff-btn"><Download size={14} className="mr-1" />Preview PDF</Button>}
                          {ffWorkflow && ffWorkflow.status === 'rejected' && (
                            <Badge variant="destructive" className="self-center">Rejected: {ffWorkflow.rejected_reason || 'No reason given'}</Badge>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="bg-muted/30 p-3 rounded text-xs text-muted-foreground italic">F&F is locked — current status: <strong>{FF_STATUS_META[ffWorkflow.status]?.label}</strong>. Withdraw the proposal to edit the figures.</div>
                    )}

                    {/* Result */}
                    {ffResult && (
                      <Card className="bg-slate-50 dark:bg-slate-900/40">
                        <CardContent className="p-4 space-y-2 text-sm">
                          <div className="flex justify-between text-xs text-muted-foreground"><span>Years of Service: {ffResult.years_of_service}</span><span>Method: {ffResult.gratuity_method}</span></div>
                          <hr className="my-2" />
                          <div className="space-y-1">
                            <div className="flex justify-between"><span>Pending Salary</span><span>{ffResult.currency} {ffResult.line_items.pending_salary.toLocaleString()}</span></div>
                            <div className="flex justify-between"><span>Leave Encashment</span><span>{ffResult.currency} {ffResult.line_items.leave_encashment.toLocaleString()}</span></div>
                            <div className="flex justify-between"><span>Gratuity / EOSB</span><span>{ffResult.currency} {ffResult.line_items.gratuity.toLocaleString()}</span></div>
                            <div className="flex justify-between"><span>Pending Bonus</span><span>{ffResult.currency} {ffResult.line_items.pending_bonus.toLocaleString()}</span></div>
                            <div className="flex justify-between font-semibold border-t pt-1"><span>Total Earnings</span><span>{ffResult.currency} {ffResult.total_earnings.toLocaleString()}</span></div>
                            <div className="flex justify-between text-red-600"><span>Outstanding Dues</span><span>−{ffResult.currency} {ffResult.line_items.dues.toLocaleString()}</span></div>
                            <div className="flex justify-between text-red-600"><span>Notice Recovery</span><span>−{ffResult.currency} {ffResult.line_items.notice_period_recovery.toLocaleString()}</span></div>
                            <div className="flex justify-between font-bold text-lg border-t-2 pt-2 mt-2"><span>NET PAYABLE</span><span className="text-emerald-700">{ffResult.currency} {ffResult.net_payable.toLocaleString()}</span></div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Approval workflow actions */}
                    {ffResult && (
                      <Card>
                        <CardHeader className="pb-2"><CardTitle className="text-sm">Approval Workflow</CardTitle></CardHeader>
                        <CardContent className="space-y-3">
                          {ffWorkflow && ffWorkflow.status === 'paid' && (
                            <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 rounded p-3 text-sm text-emerald-700 dark:text-emerald-300 flex items-start gap-2">
                              <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="font-medium">F&amp;F Settlement Paid</p>
                                <p className="text-xs">Reference: <strong>{ffWorkflow.paid_reference}</strong> via {ffWorkflow.payment_method?.replace('_', ' ')} on {ffWorkflow.paid_at?.slice(0, 10)}. A payslip entry has been auto-generated.</p>
                              </div>
                            </div>
                          )}

                          {ffWorkflow && ffWorkflow.status !== 'paid' && (
                            <Textarea placeholder="Optional comments for this transition…" rows={2} value={decisionComments} onChange={(e) => setDecisionComments(e.target.value)} data-testid="ff-decision-comments" />
                          )}

                          <div className="flex gap-2 flex-wrap">
                            {(!ffWorkflow || ffWorkflow.status === 'draft' || ffWorkflow.status === 'rejected') && (
                              <Button onClick={() => ffAction('propose')} disabled={ffActionLoading || !ffResult} data-testid="ff-propose-btn"><Send size={14} className="mr-1" />Propose for Finance Approval</Button>
                            )}
                            {ffWorkflow && ffWorkflow.status === 'proposed' && (
                              <>
                                <Button onClick={() => ffAction('approve')} disabled={ffActionLoading} data-testid="ff-approve-btn"><ThumbsUp size={14} className="mr-1" />Approve</Button>
                                <Button onClick={() => ffAction('reject')} disabled={ffActionLoading} variant="destructive" data-testid="ff-reject-btn"><ThumbsDown size={14} className="mr-1" />Reject</Button>
                                <Button onClick={() => ffAction('withdraw')} disabled={ffActionLoading} variant="outline" data-testid="ff-withdraw-btn"><Undo2 size={14} className="mr-1" />Withdraw</Button>
                              </>
                            )}
                            {ffWorkflow && ffWorkflow.status === 'finance_approved' && (
                              <>
                                <Button onClick={() => ffAction('finalize')} disabled={ffActionLoading} data-testid="ff-finalize-btn"><Lock size={14} className="mr-1" />Finalize &amp; Generate Signed PDF</Button>
                                <Button onClick={() => ffAction('withdraw')} disabled={ffActionLoading} variant="outline"><Undo2 size={14} className="mr-1" />Withdraw</Button>
                              </>
                            )}
                            {ffWorkflow && ffWorkflow.status === 'finalized' && (
                              <Button onClick={() => { setPaidForm({ paid_reference: '', payment_method: 'bank_transfer', paid_date: new Date().toISOString().split('T')[0], notes: '' }); setPaidDialog(true); }} disabled={ffActionLoading} data-testid="ff-mark-paid-btn"><BadgeCheck size={14} className="mr-1" />Mark as Paid</Button>
                            )}
                            {ffResult && <Button onClick={downloadFFPdf} variant="outline"><Download size={14} className="mr-1" />Download PDF</Button>}
                          </div>

                          {/* History */}
                          {ffWorkflow && (ffWorkflow.history || []).length > 0 && (
                            <div className="pt-3 border-t">
                              <Label className="text-xs">Approval History</Label>
                              <div className="space-y-1 mt-1 max-h-40 overflow-y-auto">
                                {ffWorkflow.history.map((h, i) => (
                                  <div key={i} className="flex items-start gap-2 text-xs border-l-2 border-primary/40 pl-2 py-1">
                                    <Badge variant="outline" className="text-[10px]">{h.action}</Badge>
                                    <div className="flex-1">
                                      <p><strong>{h.by}</strong> ({h.role?.replace('_', ' ')}) · {new Date(h.at).toLocaleString()}</p>
                                      {h.comments && <p className="text-muted-foreground italic">"{h.comments}"</p>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="interview">
                {selected.exit_interview ? (
                  <Card><CardContent className="p-4 space-y-2 text-sm">
                    <p className="text-xs text-muted-foreground">Submitted on {new Date(selected.exit_interview.submitted_at).toLocaleString()}</p>
                    <div className="grid grid-cols-5 gap-2 text-center">
                      {[['overall_rating','Overall'],['management_rating','Management'],['growth_rating','Growth'],['culture_rating','Culture'],['compensation_rating','Compensation']].map(([k,l]) => (
                        <div key={k} className="border rounded p-2"><div className="text-xs text-muted-foreground">{l}</div><div className="text-xl font-bold">{selected.exit_interview[k]}/5</div></div>
                      ))}
                    </div>
                    <div><Label className="text-xs">Primary Reason</Label><p>{selected.exit_interview.primary_reason}</p></div>
                    <div><Label className="text-xs">Feedback</Label><p className="whitespace-pre-wrap">{selected.exit_interview.feedback || '—'}</p></div>
                    <div><Label className="text-xs">Suggestions</Label><p className="whitespace-pre-wrap">{selected.exit_interview.suggestions || '—'}</p></div>
                    <div><Label className="text-xs">Would recommend?</Label><Badge variant={selected.exit_interview.would_recommend ? 'default' : 'destructive'}>{selected.exit_interview.would_recommend ? 'Yes' : 'No'}</Badge></div>
                  </CardContent></Card>
                ) : (
                  <p className="text-sm text-muted-foreground italic p-4">Employee has not submitted the exit interview yet. They can fill it from their <code>/me/resignation</code> portal.</p>
                )}
              </TabsContent>

              <TabsContent value="kt">
                {(selected.kt_documents || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground italic p-4">No KT documents uploaded yet.</p>
                ) : (
                  <div className="space-y-2">
                    {selected.kt_documents.map(d => (
                      <Card key={d.file_id}><CardContent className="p-3 flex items-center justify-between">
                        <div className="flex items-center gap-2"><FileText size={16} /><div><p className="text-sm font-medium">{d.filename}</p><p className="text-xs text-muted-foreground">{(d.size/1024).toFixed(1)} KB · uploaded {new Date(d.uploaded_at).toLocaleDateString()} by {d.uploaded_by}</p></div></div>
                        <Button size="sm" variant="outline" asChild><a href={offboardingAPI.ktDownloadUrl(selected.employee_id, d.file_id) + '?token=' + localStorage.getItem('token')} download={d.filename}><Download size={14} className="mr-1" />Download</a></Button>
                      </CardContent></Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setSelected(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog open={paidDialog} onOpenChange={setPaidDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><BadgeCheck size={18} />Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Payment Reference *</Label><Input value={paidForm.paid_reference} onChange={(e) => setPaidForm({...paidForm, paid_reference: e.target.value})} placeholder="e.g., TXN-2026-001" data-testid="paid-reference" /></div>
            <div><Label>Payment Date</Label><Input type="date" value={paidForm.paid_date} onChange={(e) => setPaidForm({...paidForm, paid_date: e.target.value})} data-testid="paid-date" /></div>
            <div>
              <Label>Payment Method</Label>
              <Select value={paidForm.payment_method} onValueChange={(v) => setPaidForm({...paidForm, payment_method: v})}>
                <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notes (optional)</Label><Textarea rows={2} value={paidForm.notes} onChange={(e) => setPaidForm({...paidForm, notes: e.target.value})} data-testid="paid-notes" /></div>
            <p className="text-xs text-muted-foreground">A payslip entry of type <code>full_and_final</code> will be auto-created and the offboarding F&amp;F task will be marked complete.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaidDialog(false)}>Cancel</Button>
            <Button onClick={() => ffAction('mark_paid')} disabled={ffActionLoading || !paidForm.paid_reference} data-testid="confirm-paid-btn">
              {ffActionLoading && <Loader2 size={14} className="animate-spin mr-1" />}<BadgeCheck size={14} className="mr-1" />Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Offboarding;
